'use client';

import React, { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';

import ButtonWithLoader from 'src/components/shared/form-fields/loading-button';
import { AuthContext } from 'src/context/auth-context/auth-context';
import type { UserDetails } from 'src/types/auth';
import { handleErrorMessages } from 'src/helpers/utils';
import { updateFeatureIntroActionStatus } from 'src/services/profile';

interface FeaturesInfoProps {
  handleCloseModal: () => void;
}

interface ActionStatus {
  actionType?: string;
  isLoading?: boolean;
}

interface ActionButtonsProps {
  actionStatus: ActionStatus;
  onActionHandler: (action: string, triggeredFrom?: string) => void;
}

const FeaturesInfo = ({ handleCloseModal }: FeaturesInfoProps) => (
  <React.Fragment>
    <ModalHeader toggle={handleCloseModal}>
      🚀 Major Product Update!
    </ModalHeader>
    <ModalBody>
      <div className="modal-body-inner">
        <p>
          We&apos;ve made huge improvements to help you manage
          requests, invoices,
          quotes, work orders, and even communicate with Lula!
        </p>
        <div className="product-update-outer">
          <div className="product-update-item">
            <h5>New Work Order Details Page</h5>
            <ul>
              <li>See a full history of key events inside the activity log.</li>
              <li>
                Each work order has its own unique url to share with your team.
              </li>
            </ul>
          </div>
          <div className="product-update-item">
            <h5>Maintenance Request Grouping</h5>
            <ul>
              <li>
                For complex issues, easily find all related jobs, invoices, and
                quotes inside their Maintenance Request (MR).
              </li>
            </ul>
          </div>
          <div className="product-update-item">
            <h5>Live Chat with Lula</h5>
            <ul>
              <li>
                Message Lula inside your maintenance requests with full history of
                your conversations.
              </li>
            </ul>
          </div>
          <div className="product-update-item">
            <h5>...And Much More!</h5>
            <ul>
              <li>Click the link below to see all of the updates in action.</li>
            </ul>
          </div>
        </div>
      </div>
    </ModalBody>
  </React.Fragment>
);

const ActionButtons = (
  { actionStatus, onActionHandler }: ActionButtonsProps,
) => {
  const isSkipDemoButtonLoading = (
    actionStatus.actionType === 'skipped'
    && actionStatus?.isLoading
  );
  const isGoToDemoButtonLoading = (
    actionStatus.actionType === 'viewed'
    && actionStatus?.isLoading
  );

  return (
    <ModalFooter>
      <div className="action-wrapper">
        <ButtonWithLoader
          color="none"
          className="grey-outline-btn"
          onClick={() => onActionHandler('skipped')}
          disabled={actionStatus?.isLoading}
          isLoading={isSkipDemoButtonLoading}
        >
          Skip Demo
        </ButtonWithLoader>

        <ButtonWithLoader
          color="none"
          className="blue-solid-btn"
          onClick={() => onActionHandler(
            'viewed',
            'goToDemoBtnClick',
          )}
          disabled={actionStatus?.isLoading}
          isLoading={isGoToDemoButtonLoading}
        >
          Go to Demo
          <svg
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 6.50001L14 2.50001M14 2.50001H9.99999M14 2.50001L8 8.5M6.66667 2.5H5.2C4.0799 2.5 3.51984 2.5 3.09202 2.71799C2.71569 2.90973 2.40973 3.21569 2.21799 3.59202C2 4.01984 2 4.57989 2 5.7V11.3C2 12.4201 2 12.9802 2.21799 13.408C2.40973 13.7843 2.71569 14.0903 3.09202 14.282C3.51984 14.5 4.07989 14.5 5.2 14.5H10.8C11.9201 14.5 12.4802 14.5 12.908 14.282C13.2843 14.0903 13.5903 13.7843 13.782 13.408C14 12.9802 14 12.4201 14 11.3V9.83333"
              stroke="white"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ButtonWithLoader>
      </div>
    </ModalFooter>
  );
};

// ====================================== //
// **** Main Component starts here **** //
// ====================================== //
const NewFeaturesIntroModal = () => {
  const [, setUserDetails] = useContext(AuthContext);

  const [actionStatus, setActionStatus] = useState<ActionStatus>({});

  const onActionHandler = (action: string, triggeredFrom?: string) => {
    const payload = { action };

    setActionStatus({ actionType: action, isLoading: true });

    updateFeatureIntroActionStatus(payload)
      .then(() => {
        // State update after API call is skipped if
        // function is triggered from modal close btn click.
        if (triggeredFrom !== 'closeBtnClick') {
          setUserDetails((
            prevUserDetails: UserDetails | null,
          ) => ({
            ...prevUserDetails,
            active_feature_intro: {},
          }) as UserDetails);

          if (triggeredFrom === 'goToDemoBtnClick') {
            window.open('https://lula.storylane.io/share/gyu1g0q90lpk', '_blank');
          }
        }
      }).catch((error: Record<string, unknown>) => {
        handleErrorMessages({
          errors: (
            (error?.message as string)
            || (error?.errors as string)
          ),
        }, (msg: string) => toast(msg, { type: 'error' }));
      })
      .finally(() => {
        if (triggeredFrom !== 'closeBtnClick') {
          setActionStatus({ actionType: action, isLoading: false });
        }
      });
  };

  const handleCloseModal = () => {
    onActionHandler('skipped', 'closeBtnClick');
    setUserDetails((
      prevUserDetails: UserDetails | null,
    ) => ({
      ...prevUserDetails,
      active_feature_intro: {},
    }) as UserDetails);
  };

  return (
    <Modal
      centered
      isOpen
      className="product-update-modal"
    >
      <FeaturesInfo
        handleCloseModal={handleCloseModal}
      />
      <ActionButtons
        actionStatus={actionStatus}
        onActionHandler={onActionHandler}
      />
    </Modal>
  );
};

export default NewFeaturesIntroModal;
